import axios from 'axios'

// Create axios instance with base configuration
const api = axios.create({
  baseURL: '/api', // Vite proxy will route to backend
  timeout: 30000, // 30 second timeout for large analysis requests
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor for debugging
api.interceptors.request.use(
  (config) => {
    console.log(`Making ${config.method?.toUpperCase()} request to ${config.url}`)
    return config
  },
  (error) => {
    console.error('Request error:', error)
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    console.error('API Error:', error.response?.data || error.message)
    
    // Handle common errors
    if (error.response?.status === 401) {
      // Handle authentication errors
      localStorage.removeItem('isAuthenticated')
      window.location.href = '/login'
    }
    
    return Promise.reject(error)
  }
)

// API Service Methods
export const analyticsAPI = {
  // Health checks
  healthCheck: () => api.get('/health'),
  detailedHealth: () => api.get('/health/detailed'),
  
  // Testing and diagnostics
  testPipeline: () => api.get('/analytics/test'),
  diagnose: () => api.get('/analytics/diagnose'),
  
  // Main analytics endpoints
  analyzeAll: () => {
    console.log('Starting full dataset analysis...')
    return api.post('/analytics/analyze-all')
  },
  
  getUserAnalysis: (userId) => api.get(`/analytics/user/${userId}`),
  
  getUserScatterData: (userId) => api.get(`/analytics/user/${userId}/scatter-data`),
  
  getAllScatterData: () => api.get('/analytics/scatter-data/all'),
  
  getResiliencePatterns: () => api.get('/analytics/resilience-patterns'),
  
  getRankings: () => api.get('/analytics/rankings'),
  
  batchAnalyzeUsers: (userIds) => api.post('/analytics/batch-users', { userIds }),
  
  getSummary: () => api.get('/analytics/summary'),
  
  // New stability-specific endpoints
  getStabilityTimeSeriesData: (userId) => api.get(`/analytics/user/${userId}/scatter-data`),
  
  getAllStabilityData: () => api.get('/analytics/scatter-data/all'),
  
  // Get all stability rankings without confidence filtering
  getAllStabilityRankings: () => api.get('/analytics/rankings?includeAllConfidence=true'),
  
  // Get user's complete resilience timeline with rolling windows
  getResilienceTimeline: (userId) => api.get(`/analytics/user/${userId}/resilience-timeline`),
  
  // Resilience driver analysis endpoints
  getResilienceDrivers: (userId, timeWindow = 'all') => api.get(`/analytics/user/${userId}/resilience-drivers?timeWindow=${timeWindow}`),

  getActiveUsers: (days = 365) => api.get(`/analytics/users/active?days=${days}`),
  
  getDriverCorrelations: (userId) => api.get(`/analytics/user/${userId}/driver-correlations`),
  
  getDriverInsights: (userId) => api.get(`/analytics/user/${userId}/driver-insights`),
  
  getPressureAnalysis: (userId) => api.get(`/analytics/user/${userId}/pressure-analysis`),
  
  getZoneAnalysis: (userId) => api.get(`/analytics/user/${userId}/zone-analysis`),
  
  getEmotionalFactorAnalysis: (userId) => api.get(`/analytics/user/${userId}/emotional-factor-analysis`),
  
  getFrequencyAnalysis: (userId) => api.get(`/analytics/user/${userId}/frequency-analysis`),
  
  batchDriverAnalysis: (userIds, timeWindow = 'all') => api.post(`/analytics/batch-driver-analysis?timeWindow=${timeWindow}`, { userIds }),

  getPopulationResilienceDrivers: (minimumEntries = 8, timeWindow = 'all') =>
    api.get(`/analytics/population/resilience-drivers?minimumEntries=${minimumEntries}&timeWindow=${timeWindow}`, {
      timeout: 300000, // 5 minutes for population analysis
    }),
  
  // New user synthetic data endpoints
  generateSyntheticEntries: (count = 1) => api.post('/analytics/new-user/generate-synthetic', { count }),
  
  analyzeNewUser: (userEntries, syntheticEntries) => api.post('/analytics/new-user/analyze', {
    userEntries,
    syntheticEntries
  }),
  
  getConfidenceLevels: () => api.get('/analytics/new-user/confidence-levels'),
}

// Authentication helpers (validated against backend ADMIN_PASSWORD)
export const authAPI = {
  login: async (password) => {
    const response = await api.post('/auth/login', { password })
    if (response.data.success) {
      localStorage.setItem('isAuthenticated', 'true')
    }
    return response.data
  },
  
  logout: () => {
    localStorage.removeItem('isAuthenticated')
  },
  
  isAuthenticated: () => {
    return localStorage.getItem('isAuthenticated') === 'true'
  },
}

// Helper functions for data processing
export const dataHelpers = {
  // Format score with interpretation
  formatScore: (score, metric) => {
    const rounded = Math.round(score)
    let interpretation = ''
    
    switch (metric) {
      case 'resilience':
        if (rounded >= 85) interpretation = 'Superhuman'
        else if (rounded >= 50) interpretation = 'Moderately affected by pressure'
        else if (rounded >= 25) interpretation = 'Notably decreased by pressure'
        else interpretation = 'Emotions crash under pressure'
        break
        
      case 'superhumans':
        interpretation = 'Exceptional users'
        break
        
      case 'stability':
        if (rounded >= 80) interpretation = 'Very consistent emotions'
        else if (rounded >= 60) interpretation = 'Generally stable'
        else if (rounded >= 40) interpretation = 'Moderate fluctuation'
        else if (rounded >= 20) interpretation = 'High variability'
        else interpretation = 'Very unstable emotions'
        break
        
      case 'health':
        if (rounded >= 80) interpretation = 'Excellent baseline health'
        else if (rounded >= 60) interpretation = 'Good baseline health'
        else if (rounded >= 40) interpretation = 'Fair baseline health'
        else if (rounded >= 20) interpretation = 'Poor baseline health'
        else interpretation = 'Very poor baseline health'
        break
        
      default:
        interpretation = 'Unknown metric'
    }
    
    return { score: rounded, interpretation }
  },
  
  // Get status category for styling
  getStatusCategory: (score) => {
    if (score >= 80) return 'excellent'
    if (score >= 60) return 'good'
    if (score >= 40) return 'fair'
    return 'poor'
  },
  
  // Format large numbers
  formatNumber: (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
    return num.toString()
  },
  
  // Sort users by metric
  sortUsersByMetric: (users, metric, direction = 'desc') => {
    return [...users].sort((a, b) => {
      const aScore = a.metrics?.[metric]?.score || 0
      const bScore = b.metrics?.[metric]?.score || 0
      return direction === 'desc' ? bScore - aScore : aScore - bScore
    })
  },
}

export default api 